/**
 * API Client - Axios instance with interceptors
 *
 * Features:
 * - Automatic token refresh on 401
 * - Retry with exponential backoff for network errors
 * - Request queuing during token refresh
 * - Connection status tracking for UI feedback
 */
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { getFirebaseAuth } from '@/plugins/firebase'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// Connection status event emitter for UI feedback
type ConnectionEventType = 'retry-start' | 'retry-end' | 'connected'
type ConnectionEventPayload = {
  reason?: 'network' | 'server'
  count?: number
  success?: boolean
}
type ConnectionEventListener = (payload: ConnectionEventPayload) => void

const connectionListeners: Map<ConnectionEventType, Set<ConnectionEventListener>> = new Map()

export const connectionEvents = {
  on(event: ConnectionEventType, listener: ConnectionEventListener) {
    if (!connectionListeners.has(event)) {
      connectionListeners.set(event, new Set())
    }
    connectionListeners.get(event)!.add(listener)
    return () => connectionListeners.get(event)?.delete(listener)
  },
  emit(event: ConnectionEventType, payload: ConnectionEventPayload = {}) {
    connectionListeners.get(event)?.forEach((listener) => listener(payload))
  },
}

// Token refresh state
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: Error) => void
}> = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token and workspace context
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const auth = getFirebaseAuth()
    if (auth?.currentUser) {
      try {
        // Get fresh token (Firebase SDK caches this internally)
        const token = await auth.currentUser.getIdToken()
        config.headers.Authorization = `Bearer ${token}`
      } catch (error) {
        console.error('[API] Error getting auth token:', error)
        // Don't block the request - let the server return 401
      }
    }

    // Add workspace context header if available
    // This is read from localStorage to avoid circular dependency with stores
    const workspaceCompanyId = localStorage.getItem('workspace_company_id')
    if (workspaceCompanyId) {
      config.headers['X-Workspace-Company-Id'] = workspaceCompanyId
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors with smart retry
apiClient.interceptors.response.use(
  (response) => {
    // Mark connection as established on any successful response
    connectionEvents.emit('connected')
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      _retryCount?: number
    }

    // Handle 401 — token expired or invalid.
    // We only treat it as an auth failure if the response body is shaped like our
    // auth_middleware's response (error: 'unauthorized'). Some route handlers
    // misuse 401 for non-auth reasons (e.g. "QuickBooks not connected"); those
    // would otherwise force a token refresh + logout flow when the user's session
    // is actually fine.
    const respData = error.response?.data as { error?: string } | undefined
    const isRealAuthFailure = respData?.error === 'unauthorized'

    if (error.response?.status === 401 && isRealAuthFailure && !originalRequest._retry) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(apiClient(originalRequest))
            },
            reject: (err: Error) => {
              reject(err)
            },
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const auth = getFirebaseAuth()

      if (auth?.currentUser) {
        try {
          // Force refresh the token
          const token = await auth.currentUser.getIdToken(true)
          console.log('[API] Token refreshed successfully')

          // Process queued requests
          processQueue(null, token)

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`

          try {
            return await apiClient(originalRequest)
          } catch (retryError) {
            // If retry still fails with 401, force logout
            const axiosError = retryError as AxiosError
            if (axiosError.response?.status === 401) {
              console.log('[API] Retry still got 401, session is invalid. Signing out...')
              await auth.signOut()
              window.location.href = '/login'
              return Promise.reject(retryError)
            }
            throw retryError
          }
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError)
          processQueue(refreshError as Error, null)

          // Check if this is a network error vs auth error
          if ((refreshError as Error).message?.includes('network')) {
            console.error('[API] Network error during token refresh')
          } else {
            // Auth truly failed - sign out and redirect to login
            console.log('[API] Auth failed, signing out and redirecting to login...')
            try {
              await auth.signOut()
            } catch {
              // Ignore signout errors
            }
            window.location.href = '/login'
          }
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      } else {
        isRefreshing = false
        // No current user - redirect to login
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    // Handle network errors with retry
    if (!error.response && !originalRequest._retry) {
      const retryCount = originalRequest._retryCount || 0

      if (retryCount < 2) {
        originalRequest._retryCount = retryCount + 1
        console.log(`[API] Network error, retrying (${retryCount + 1}/2)...`)

        // Emit retry event for UI
        connectionEvents.emit('retry-start', { reason: 'network', count: retryCount + 1 })

        // Exponential backoff
        await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)))

        try {
          const result = await apiClient(originalRequest)
          connectionEvents.emit('retry-end', { reason: 'network', count: retryCount + 1, success: true })
          return result
        } catch (retryError) {
          if ((originalRequest._retryCount || 0) >= 2) {
            connectionEvents.emit('retry-end', { reason: 'network', count: retryCount + 1, success: false })
          }
          throw retryError
        }
      }
    }

    // Handle 503 Service Unavailable with retry (Cloud Run cold start)
    // Cloud Run cold starts can take 30-60 seconds, so we retry up to 8 times
    if (error.response?.status === 503) {
      const retryCount = originalRequest._retryCount || 0
      const maxRetries = 8

      if (retryCount < maxRetries) {
        originalRequest._retryCount = retryCount + 1
        console.log(`[API] Service unavailable, retrying (${retryCount + 1}/${maxRetries})...`)

        // Emit retry event for UI
        connectionEvents.emit('retry-start', { reason: 'server', count: retryCount + 1 })

        // Wait 5 seconds per retry (total ~40s wait for 8 retries)
        await new Promise((resolve) => setTimeout(resolve, 5000))

        try {
          const result = await apiClient(originalRequest)
          connectionEvents.emit('retry-end', { reason: 'server', count: retryCount + 1, success: true })
          return result
        } catch (retryError) {
          if ((originalRequest._retryCount || 0) >= maxRetries) {
            connectionEvents.emit('retry-end', { reason: 'server', count: retryCount + 1, success: false })
          }
          throw retryError
        }
      }
    }

    // Extract error message for logging
    const errorData = error.response?.data as { error?: string; message?: string; hint?: string }
    const message = errorData?.message || errorData?.error || error.message
    const hint = errorData?.hint

    if (hint) {
      console.error('[API] Error:', message, '- Hint:', hint)
    } else {
      console.error('[API] Error:', message)
    }

    // Record API error for feedback reports
    try {
      const { recordApiError } = await import('@/services/error-tracker')
      recordApiError(
        originalRequest.url || 'unknown',
        error.response?.status || 0,
        message
      )
    } catch {
      // Ignore if error tracker not available
    }

    return Promise.reject(error)
  }
)

export default apiClient

// Export API modules
export * from './auth.api'
export * from './users.api'
export * from './contacts.api'
export * from './companies.api'
export * from './deals.api'
export * from './projects.api'
export * from './tasks.api'
export * from './time-entries.api'
export * from './proposals.api'
export * from './invoices.api'
export * from './activities.api'
export * from './dashboard.api'
export * from './reports.api'
export * from './microsoft.api'
export * from './client-portal.api'
export * from './admin.api'
export * from './email-campaigns.api'
export * from './branding.api'
export * from './pipeline.api'
export * from './notes.api'
export * from './sales-tasks.api'
export * from './search.api'
export * from './notifications.api'
export * from './email-tracking.api'
export * from './automations.api'
export * from './meeting-scheduler.api'
export * from './document-tracking.api'
export * from './call-logs.api'
export * from './lead-scoring.api'
export * from './bulk-actions.api'
export * from './ai.api'
export * from './social.api'
export * from './analytics.api'
export * from './intelligence.api'
export * from './sequences.api'
export * from './impersonation.api'
// Note: These modules have conflicting exports with other modules
// Import them directly where needed instead of re-exporting
// export * from './adPlatforms.api'
// export * from './google.api'
// export * from './integrations.api'
// export * from './messaging.api'
// export * from './referrals.api'
// export * from './forms.api'
